/**
 * AgreementVersionPanel — which engagement agreement this client is on (Lloyd, Aug 21 2026).
 *
 * Shows the version, the signature date, who recorded it, and `describeAgreement`'s plain-English
 * reading of whether the newer terms (the failure-to-appear fee included) apply. Staff can change
 * it here: a version without a signature date is accepted and stored, but the reading still says the
 * original terms apply and the signature is awaited — because a box ticked when the addendum goes
 * OUT is the likeliest way this record goes wrong.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AgreementApi, {
  AGREEMENT_LABELS,
  AgreementVersion,
  ClientAgreementDto,
} from '../../api/AgreementApi';

interface AgreementVersionPanelProps {
  User_guid: string;
}

const VERSIONS: AgreementVersion[] = ['LEGACY', 'ADDENDUM', 'NEW_AGREEMENT'];

const AgreementVersionPanel: React.FC<AgreementVersionPanelProps> = ({ User_guid }) => {
  const [dto, setDto] = useState<ClientAgreementDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [version, setVersion] = useState<AgreementVersion>('LEGACY');
  const [signedAt, setSignedAt] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await AgreementApi.get(User_guid);
      setDto(result);
      setVersion(result.User_AgreementVersion);
      setSignedAt(result.User_AgreementSignedAt ?? '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the agreement');
    } finally {
      setLoading(false);
    }
  }, [User_guid]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const result = await AgreementApi.record(User_guid, {
        User_AgreementVersion: version,
        // LEGACY never carries a date; the backend clears it anyway, this just keeps the form honest.
        User_AgreementSignedAt: version === 'LEGACY' ? null : signedAt || null,
      });
      setDto(result);
      setVersion(result.User_AgreementVersion);
      setSignedAt(result.User_AgreementSignedAt ?? '');
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record the agreement');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">
          Loading the agreement…
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
        Engagement agreement
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {dto && !editing && (
        <Stack spacing={1}>
          <Box>
            <Chip
              size="small"
              color={dto.status.newTermsApply ? 'success' : dto.status.awaitingSignature ? 'warning' : 'default'}
              label={
                // A portal acceptance is not a signed document. Checked by the absence of a real
                // signature date rather than the stored version being LEGACY, because a client can
                // have ADDENDUM or NEW_AGREEMENT recorded with no signature yet (the addendum went
                // out and has not come back) and still be the terms applying only because they
                // clicked accept in the portal — that case would otherwise show "Addendum signed"
                // for someone who never signed anything.
                dto.status.newTermsApply && !dto.User_AgreementSignedAt
                  ? 'Terms accepted in the portal'
                  : AGREEMENT_LABELS[dto.User_AgreementVersion]
              }
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {dto.status.reason}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {dto.User_AgreementSignedAt
              ? `Signed ${new Date(dto.User_AgreementSignedAt).toLocaleDateString('en-US')}`
              : 'No signature date on record'}
            {dto.User_AgreementRecordedBy ? ` · recorded by ${dto.User_AgreementRecordedBy}` : ''}
          </Typography>
          <Box>
            <Button size="small" onClick={() => setEditing(true)}>
              Change
            </Button>
          </Box>
        </Stack>
      )}

      {editing && (
        <Stack spacing={2} sx={{ maxWidth: 420 }}>
          <TextField
            select
            label="Agreement version"
            size="small"
            value={version}
            onChange={(e) => setVersion(e.target.value as AgreementVersion)}
          >
            {VERSIONS.map((v) => (
              <MenuItem key={v} value={v}>
                {AGREEMENT_LABELS[v]}
              </MenuItem>
            ))}
          </TextField>
          {version !== 'LEGACY' && (
            <TextField
              label="Signature date"
              type="date"
              size="small"
              value={signedAt}
              onChange={(e) => setSignedAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Leave blank if it has been sent but not signed — the newer terms will not apply yet"
            />
          )}
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button
              onClick={() => {
                setEditing(false);
                setVersion(dto?.User_AgreementVersion ?? 'LEGACY');
                setSignedAt(dto?.User_AgreementSignedAt ?? '');
              }}
              disabled={saving}
            >
              Cancel
            </Button>
          </Stack>
        </Stack>
      )}
    </Box>
  );
};

export default AgreementVersionPanel;
